import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Modal, Stack, Select, Button, Group, Text, Loader, Center, Alert } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { storyService } from '../../services/storyService'
import { useAddStoryToTeam } from '../../hooks/useTeam'

/**
 * Facilitator-only. Lists only stories the facilitator has access to via
 * a Group (`in_user_group`) AND that aren't already in the team's history.
 * Public stories are filtered out — Teams are Group-scoped by design.
 */
export function AddStoryModal({ teamId, opened, onClose, excludeStoryIds = [] }) {
  const [storyId, setStoryId] = useState(null)
  const { data: stories, isLoading } = useQuery({
    queryKey: ['stories'],
    queryFn: () => storyService.getStories(),
    enabled: opened,
  })

  const addStory = useAddStoryToTeam(teamId)

  const eligibleStories = useMemo(() => {
    return (stories || []).filter((s) => {
      if (!s.in_user_group) return false
      if (s.threads_count === 0) return false
      if (excludeStoryIds.includes(s.id)) return false
      return true
    })
  }, [stories, excludeStoryIds])

  const handleSubmit = async () => {
    if (!storyId) return
    await addStory.mutateAsync(storyId)
    setStoryId(null)
    onClose()
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Add a story to this team"
      centered
      styles={{
        content: { background: 'rgba(10, 22, 40, 0.95)', borderColor: 'rgba(0, 255, 255, 0.15)' },
        header: { background: 'rgba(10, 22, 40, 0.95)' },
        title: { color: 'var(--mantine-color-cyan-3)', fontFamily: 'Orbitron, sans-serif' },
      }}
    >
      <Stack gap="md">
        {isLoading ? (
          <Center py="lg"><Loader color="cyan" /></Center>
        ) : eligibleStories.length === 0 ? (
          <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light">
            No additional stories are available in your Group.
          </Alert>
        ) : (
          <>
            <Text size="sm" c="dimmed">
              Adding a story marks the current one complete and starts the new one.
              Members who lack access will be set inactive for the new story.
            </Text>
            <Select
              label="Story"
              placeholder="Pick a story"
              data={eligibleStories.map((s) => ({ value: String(s.id), label: s.name }))}
              value={storyId}
              onChange={setStoryId}
              required
              styles={{
                input: {
                  background: 'rgba(10, 22, 40, 0.8)',
                  border: '1px solid rgba(0, 255, 255, 0.15)',
                  color: '#e0e8f0',
                },
                label: { color: 'var(--mantine-color-gray-4)' },
              }}
            />
            <Group justify="flex-end">
              <Button variant="subtle" color="gray" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} loading={addStory.isPending} disabled={!storyId}>
                Add Story
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  )
}
